import { useState } from 'react';
import Card from '../../../components/card/Card';
import H from '../../../components/texts/h/H';
import P from '../../../components/texts/p/P';
import Icon from '../../../components/icon/Icon';
import Spinner from '../../../components/Spinner/spinner';
import { clientData, documentData, applicationFile } from '../../../types/interfaces';
import {
  downloadClientDocument,
  downloadLufeCloudFile,
} from '../../../services/ClientService';
import { downloadSignerDocument } from '../../../services/ApplicationService';
import { getMimeType } from '../../../utils/Utils';
import { DOCUMENT_IDS } from '../../../utils/Constants';
import styled from 'styled-components';
import { useClientTheme } from '../../../context/ThemeContext';

const DocumentsGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DocumentCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #ffffff;
  box-sizing: border-box;
  min-width: 0;
`;

const DocumentIconContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const DocumentLabelContainer = styled.div`
  flex: 1;
  min-width: 0;
  overflow: hidden;
`;

const DocumentActionContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  cursor: pointer;
`;

interface DocumentsReadOnlyProps {
  clientData: clientData;
  cuit: string | undefined;
  applicationId: string | undefined;
}

const DocumentsReadOnly: React.FC<DocumentsReadOnlyProps> = ({
  clientData,
  cuit,
  applicationId,
}) => {
  const { clientTheme } = useClientTheme();
  const [isLoading, setIsLoading] = useState<Record<string, boolean>>({});
  
  const allowedTypes = [
    '006',
    '011',
    '012',
    '013',
    '014',
    '015',
    '016',
    '017',
    '018',
    '019',
  ];

  const handleDownloadFile = async (
    filename: string,
    fileType: string,
    isSignerDocument: boolean = false
  ) => {
    const loadingKey = `${fileType}-${filename}`;
    setIsLoading((prev) => ({ ...prev, [loadingKey]: true }));

    try {
      let response;
      
      if (isSignerDocument && applicationId && cuit) {
        response = await downloadSignerDocument(applicationId, cuit, filename);
      } else if (!filename?.includes('.') && cuit) {
        response = await downloadLufeCloudFile(cuit, filename);
      } else if (cuit) {
        response = await downloadClientDocument(cuit, filename);
      }

      if (response?.success) {
        const mimeType = getMimeType(filename);
        const fileBlob = new Blob([response.data], { type: mimeType });
        const url = window.URL.createObjectURL(fileBlob);

        if (
          mimeType.startsWith('application/pdf') ||
          mimeType.startsWith('image/')
        ) {
          window.open(url, '_blank');
        } else {
          const a = document.createElement('a');
          a.href = url;
          a.download = filename;
          a.click();
          window.URL.revokeObjectURL(url);
        }
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    } finally {
      setIsLoading((prev) => ({ ...prev, [loadingKey]: false }));
    }
  };

  // Obtener todos los documentos de instrumentación
  const getAllInstrumentationDocuments = () => {
    const documents: Array<{
      type: string;
      filename: string;
      label: string;
      isSignerDocument: boolean;
    }> = [];
    const seenDocuments = new Set<string>();

    // Documentos de clientData.application_files (documentos de instrumentación)
    if (clientData.application_files) {
      clientData.application_files.forEach((file: applicationFile) => {
        // Solo procesar archivos de tipos permitidos
        if (!allowedTypes.includes(file.file_type)) return;
        
        // Verificar si es un documento de firmante (013, 014) o general (015-019)
        const isSignerDoc = ['013', '014'].includes(file.file_type);
        
        // Extraer el nombre del archivo del asset_file_path
        const filename = file.asset_file_path?.split('/').pop() || file.asset_file_path || '';
        const docKey = `${file.file_type}-${filename}`;
        
        // Evitar duplicados
        if (!seenDocuments.has(docKey) && filename) {
          seenDocuments.add(docKey);
          
          // Para DNI de firmantes, crear label personalizado
          let label = DOCUMENT_IDS[file.file_type] || file.file_type;
          if (isSignerDoc && clientData.signers) {
            // Buscar el firmante correspondiente por CUIT en el filename
            const signer = clientData.signers.find((s) => 
              filename.includes(s.cuit) || file.asset_file_path?.includes(s.cuit)
            );
            if (signer) {
              label = file.file_type === '013' 
                ? `DNI frente de ${signer.name || 'firmante'}`
                : `DNI dorso de ${signer.name || 'firmante'}`;
            }
          }
          
          documents.push({
            type: file.file_type,
            filename,
            label,
            isSignerDocument: isSignerDoc,
          });
        }
      });
    }

    // Documentos de clientData.files que pueden estar relacionados con instrumentación
    // (011 Estatuto, 012 Actas, 006 Certificado Pyme, y otros)
    if (clientData.files) {
      clientData.files.forEach((file: documentData) => {
        // Solo procesar archivos de tipos permitidos
        if (!allowedTypes.includes(file.type)) return;
        
        const docKey = `${file.type}-${file.file_name}`;
        
        // Evitar duplicados con application_files
        if (!seenDocuments.has(docKey) && file.file_name) {
          seenDocuments.add(docKey);
          
          const label = DOCUMENT_IDS[file.type] || file.type;
          documents.push({
            type: file.type,
            filename: file.file_name,
            label,
            isSignerDocument: false,
          });
        }
      });
    }

    return documents;
  };

  const documents = getAllInstrumentationDocuments();

  // Separar DNI de firmantes del resto
  const dniDocuments = documents.filter((doc) => ['013', '014'].includes(doc.type));
  const otherDocuments = documents.filter((doc) => !['013', '014'].includes(doc.type));

  return (
    <Card margin={{ top: '4' }}>
      <Card variant={clientTheme ? 'primary' : 'success'} modifier='weak' spacing={'2'}>
        <H
          type='3'
          variant='neutral-1'
          withMargin={false}
          weight='bold'
          modifier='strong'
        >
          Documentación
        </H>
      </Card>

      {/* DNI de firmantes */}
      {dniDocuments.length > 0 && (
        <Card margin={{ top: '4' }} width='100%'>
          <DocumentsGridContainer>
            {dniDocuments.map((doc, index) => {
              const loadingKey = `${doc.type}-${doc.filename}`;
              return (
                <DocumentCard key={index}>
                  <DocumentIconContainer>
                    <Icon variant='neutral' name='document_outline' height={24} width={24} />
                  </DocumentIconContainer>
                  <DocumentLabelContainer>
                    <P
                      type='3'
                      withMargin={false}
                      variant='neutral'
                      modifier='strong'
                    >
                      {doc.label}
                    </P>
                  </DocumentLabelContainer>
                  <DocumentActionContainer>
                    {isLoading[loadingKey] ? (
                      <Spinner size='sm' />
                    ) : (
                      <Icon
                        name='view_outline'
                        variant='neutral'
                        height={24}
                        width={24}
                        cursor='pointer'
                        onClick={() =>
                          handleDownloadFile(doc.filename, doc.type, doc.isSignerDocument)
                        }
                      />
                    )}
                  </DocumentActionContainer>
                </DocumentCard>
              );
            })}
          </DocumentsGridContainer>
        </Card>
      )}

      {/* Otros documentos */}
      {otherDocuments.length > 0 && (
        <Card margin={{ top: '4' }} width='100%'>
          <DocumentsGridContainer>
            {otherDocuments.map((doc, index) => {
              const loadingKey = `${doc.type}-${doc.filename}`;
              return (
                <DocumentCard key={index}>
                  <DocumentIconContainer>
                    <Icon variant='neutral' name='document_outline' height={24} width={24} />
                  </DocumentIconContainer>
                  <DocumentLabelContainer>
                    <P
                      type='3'
                      withMargin={false}
                      variant='neutral'
                      modifier='strong'
                    >
                      {doc.label}
                    </P>
                  </DocumentLabelContainer>
                  <DocumentActionContainer>
                    {isLoading[loadingKey] ? (
                      <Spinner size='sm' />
                    ) : (
                      <Icon
                        name='view_outline'
                        variant='neutral'
                        height={24}
                        width={24}
                        cursor='pointer'
                        onClick={() =>
                          handleDownloadFile(doc.filename, doc.type, doc.isSignerDocument)
                        }
                      />
                    )}
                  </DocumentActionContainer>
                </DocumentCard>
              );
            })}
          </DocumentsGridContainer>
        </Card>
      )}

      {documents.length === 0 && (
        <Card margin={{ top: '4' }}>
          <P type='2' variant='neutral' modifier='weak' withMargin={false}>
            No hay documentos cargados
          </P>
        </Card>
      )}
    </Card>
  );
};

export default DocumentsReadOnly;

